import type { Phase } from '@/types/learning';

import { C } from './colors';

/**
 * All 50 learning steps organised into 7 phases.
 * Each step carries its own metadata: tag, description, origin marker, and optional note.
 */
export const PHASES: Phase[] = [
  {
    id: 'foundation',
    label: 'Foundation',
    color: C.sky,
    icon: '◈',
    desc: 'Core CS, Python, ML fundamentals. Non-negotiable ground floor.',
    steps: [
      {
        id: 1,
        title: 'Python Mastery for AI',
        short: 'Python',
        tag: 'Python',
        desc: 'Data structures, OOP, virtual environments — the language of the field.',
        origin: 'original',
      },
      {
        id: 2,
        title: 'Core CS Principles',
        short: 'CS Basics',
        tag: 'CS',
        desc: 'Logic, loops, complexity — needed to reason about algorithms, not just run them.',
        origin: 'original',
      },
      {
        id: 3,
        title: 'Statistics & Probability',
        short: 'Stats/Prob',
        tag: 'Math',
        desc: 'Distributions, Bayes theorem, hypothesis testing — the substrate of trust.',
        origin: 'original',
      },
      {
        id: 4,
        title: 'Linear Algebra for ML',
        short: 'Lin. Algebra',
        tag: 'Math',
        desc: "Vectors, matrices, eigenvalues — BitNet's ternary weights only make sense here.",
        origin: 'original',
      },
      {
        id: 5,
        title: 'ML Fundamentals & Taxonomy',
        short: 'ML Basics',
        tag: 'ML',
        desc: 'Supervised, unsupervised, reinforcement — know when each applies and why.',
        origin: 'original',
      },
      {
        id: 6,
        title: 'Model Evaluation Metrics',
        short: 'Eval Metrics',
        tag: 'ML',
        desc: 'F1, AUC, calibration curves, ECE — directly feeds MP-B Calibration Auditor.',
        origin: 'original',
      },
      {
        id: 7,
        title: 'Training vs. Testing vs. Overfitting',
        short: 'Train/Test',
        tag: 'ML',
        desc: 'Bias-variance tradeoff, cross-validation, data leakage — essential before real data.',
        origin: 'original',
      },
    ],
  },
  {
    id: 'classical_ml',
    label: 'Classical ML',
    color: C.violet,
    icon: '◇',
    desc: 'Interpretable models first — tree-based and linear are the trust benchmarks.',
    steps: [
      {
        id: 8,
        title: 'Key ML Algorithms',
        short: 'ML Algos',
        tag: 'ML',
        desc: "Linear regression, trees, SVMs, clustering — know what's happening inside.",
        origin: 'original',
      },
      {
        id: 9,
        title: 'Ensemble Methods',
        short: 'Ensembles',
        tag: 'ML',
        desc: 'Random Forest, XGBoost, Gradient Boosting — tabular trust benchmarks.',
        origin: 'original',
      },
      {
        id: 10,
        title: 'Build a Baseline ML Project',
        short: 'ML Project',
        tag: 'Project',
        desc: 'scikit-learn end-to-end: ingest → train → evaluate → persist. CDDBS template.',
        origin: 'original',
      },
      {
        id: 11,
        title: 'Feature Engineering & Preprocessing',
        short: 'Features',
        tag: 'ML',
        desc: 'Normalisation, encoding, pipelines — garbage in, garbage out for trust systems.',
        origin: 'original',
      },
      {
        id: 12,
        title: 'Model Tuning & Hyperparameters',
        short: 'Tuning',
        tag: 'ML',
        desc: 'Grid search, Optuna, regularisation, dropout — calibration-aware tuning.',
        origin: 'original',
      },
      {
        id: 13,
        title: 'Explainability: LIME & SHAP (Early)',
        short: 'LIME/SHAP',
        tag: 'Trust ★',
        desc: 'Local and global explanations NOW, before deep learning obscures everything.',
        origin: 'moved-earlier',
        note: 'Moved from step 37 — interpretability should precede deep learning, not follow it',
      },
    ],
  },
  {
    id: 'deep_learning',
    label: 'Deep Learning',
    color: C.cyan,
    icon: '🔬',
    desc: 'Neural networks, CNNs, transformers — the substrate BitNet and CV are built on.',
    steps: [
      {
        id: 14,
        title: 'Neural Networks: Perceptrons to Layers',
        short: 'NNs',
        tag: 'DL',
        desc: 'Activation functions, backprop, loss — no shortcuts here.',
        origin: 'original',
      },
      {
        id: 15,
        title: 'Deep Learning Frameworks',
        short: 'DL Frameworks',
        tag: 'DL',
        desc: 'PyTorch primary (hooks = Transparency Probe), TensorFlow secondary.',
        origin: 'original',
      },
      {
        id: 16,
        title: 'CNNs for Vision',
        short: 'CNNs',
        tag: 'CV',
        desc: 'Convolutions, pooling, ResNet, EfficientNet — feeds CDDBS CV module directly.',
        origin: 'original',
      },
      {
        id: 17,
        title: 'RNNs & Sequence Models',
        short: 'RNNs',
        tag: 'DL',
        desc: 'LSTMs, GRUs — context for why transformers replaced them.',
        origin: 'original',
      },
      {
        id: 18,
        title: 'Implement a Deep Learning Project',
        short: 'DL Project',
        tag: 'Project',
        desc: 'Image classification end-to-end — hands-on before SageMaker.',
        origin: 'original',
      },
      {
        id: 19,
        title: 'Transfer Learning & Pre-trained Models',
        short: 'Transfer',
        tag: 'DL',
        desc: 'BERT, ResNet, EfficientNet fine-tuning — SageMaker CV depends on this.',
        origin: 'original',
      },
      {
        id: 20,
        title: 'Computer Vision Fundamentals (OpenCV)',
        short: 'OpenCV',
        tag: 'CV',
        desc: 'Image processing, filters, channels — CDDBS visual pipeline preprocessing.',
        origin: 'original',
      },
      {
        id: 21,
        title: 'Build a CV Project with CNNs',
        short: 'CV Project',
        tag: 'Project',
        desc: 'Object detection or classification — portfolio piece + CDDBS prototype.',
        origin: 'original',
      },
    ],
  },
  {
    id: 'nlp',
    label: 'NLP & LLMs',
    color: C.green,
    icon: '◎',
    desc: 'Language models, transformers, and the architecture BitNet lives inside.',
    steps: [
      {
        id: 22,
        title: 'NLP Basics',
        short: 'NLP Basics',
        tag: 'NLP',
        desc: 'Tokenisation, embeddings, word2vec — the vocabulary before LLMs.',
        origin: 'original',
      },
      {
        id: 23,
        title: 'NLP Libraries in Practice',
        short: 'spaCy/HF',
        tag: 'NLP',
        desc: 'spaCy, NLTK, HuggingFace — where you pull BitNet-compatible weights.',
        origin: 'original',
      },
      {
        id: 24,
        title: 'Build a Basic NLP Model',
        short: 'NLP Project',
        tag: 'Project',
        desc: 'Text classification, summarisation — feeds CDDBS Stage 1 pipeline.',
        origin: 'original',
      },
      {
        id: 25,
        title: 'LLM Architecture: Attention & Transformers',
        short: 'Transformers',
        tag: 'LLM',
        desc: 'Self-attention, multi-head — the internals you probe in the Transparency Probe.',
        origin: 'original',
      },
      {
        id: 26,
        title: '1-Bit LLMs: BitNet Architecture',
        short: 'BitNet',
        tag: 'LLM ★',
        desc: 'Ternary weights (−1,0,+1), INT8 activations, CPU-only — foundation of Project 1.',
        origin: 'added',
        note: 'Added: BitNet-specific deep dive not in original list',
      },
      {
        id: 27,
        title: 'Build an NLP/LLM Project (HuggingFace)',
        short: 'LLM Project',
        tag: 'Project',
        desc: 'Fine-tuning, prompt engineering, evaluation — publishable to cddbs-research.',
        origin: 'original',
      },
      {
        id: 28,
        title: 'Generative Models: GANs, VAEs, Diffusion',
        short: 'GenAI',
        tag: 'GenAI',
        desc: 'How synthetic images are generated — informs deepfake detection in CV module.',
        origin: 'original',
      },
      {
        id: 29,
        title: 'Data Annotation & Augmentation',
        short: 'Annotation',
        tag: 'Data',
        desc: 'Labelling, weak supervision, augmentation — CDDBS corpus quality depends on this.',
        origin: 'original',
      },
    ],
  },
  {
    id: 'ai_trust',
    label: 'AI Trust Core',
    color: C.orange,
    icon: '⬡',
    desc: 'The career destination. Interpretability, safety, ethics, regulation.',
    steps: [
      {
        id: 30,
        title: 'Explainable AI (XAI) in Depth',
        short: 'XAI',
        tag: 'Trust ★',
        desc: 'Saliency maps, concept explanations, attention viz — research-level interpretability.',
        origin: 'original',
      },
      {
        id: 31,
        title: 'AI Calibration & Uncertainty Quantification',
        short: 'Calibration',
        tag: 'Trust ★',
        desc: 'Temperature scaling, conformal prediction — MP-B intro; this goes deeper.',
        origin: 'added',
        note: 'Added: calibration deserves its own step at this depth',
      },
      {
        id: 32,
        title: 'Robustness & Adversarial ML',
        short: 'Adversarial',
        tag: 'Trust ★',
        desc: 'Adversarial examples, certified robustness, red-teaming — disinformation IS adversarial.',
        origin: 'added',
        note: 'Added: adversarial robustness is core to CDDBS threat model',
      },
      {
        id: 33,
        title: 'AI Fairness & Bias Auditing',
        short: 'Fairness',
        tag: 'Ethics ★',
        desc: 'Demographic parity, equalised odds, bias pipelines — EU AI Act compliance.',
        origin: 'original',
      },
      {
        id: 34,
        title: 'Privacy in AI: DP & Federated Learning',
        short: 'Privacy/FL',
        tag: 'Trust ★',
        desc: 'DP-SGD, federated training, secure aggregation — BYOK theory.',
        origin: 'added',
        note: 'Added: privacy-preserving ML is central to offline AI trust',
      },
      {
        id: 35,
        title: 'EU AI Act & Regulatory Landscape',
        short: 'EU AI Act',
        tag: 'Regulation ★',
        desc: 'Article 13, high-risk classification, conformity — non-negotiable for Germany/EU.',
        origin: 'added',
        note: 'Added: required for AI trust career in Germany/EU',
      },
      {
        id: 36,
        title: 'AI Red-Teaming & Safety Testing',
        short: 'Red-Teaming',
        tag: 'Safety ★',
        desc: 'Structured red-teaming, jailbreak taxonomies, safety benchmarks.',
        origin: 'added',
        note: 'Added: safety testing is distinct from model evaluation',
      },
      {
        id: 37,
        title: 'Ethical Implications of AI',
        short: 'Ethics',
        tag: 'Ethics ★',
        desc: 'Misuse vectors, dual-use dilemmas, power concentration — the philosophical foundation.',
        origin: 'original',
      },
    ],
  },
  {
    id: 'infra',
    label: 'MLOps & Infra',
    color: C.yellow,
    icon: '▦',
    desc: 'Deployment, monitoring, production systems — where trust goes to die if ignored.',
    steps: [
      {
        id: 38,
        title: 'Cloud ML: AWS SageMaker',
        short: 'SageMaker',
        tag: 'Cloud',
        desc: 'Training jobs, endpoints — CDDBS CV module. Fraunhofer AISEC uses AWS.',
        origin: 'original',
      },
      {
        id: 39,
        title: 'Model Serving: FastAPI / Flask',
        short: 'FastAPI',
        tag: 'Infra',
        desc: 'REST endpoints, async inference — Pi 5 FastAPI endpoints are your local version.',
        origin: 'original',
      },
      {
        id: 40,
        title: 'Containerisation: Docker & Deployment',
        short: 'Docker',
        tag: 'Infra',
        desc: 'Docker, compose, cloud deployment — edge node is bare-metal but CI uses containers.',
        origin: 'original',
      },
      {
        id: 41,
        title: 'MLOps: CI/CD, Pipelines, Monitoring',
        short: 'MLOps',
        tag: 'MLOps ★',
        desc: 'DVC, MLflow, drift detection — Encoway CI doc drift work is a prototype.',
        origin: 'original',
      },
      {
        id: 42,
        title: 'AutoML & Hyperparameter Optimisation',
        short: 'AutoML',
        tag: 'ML',
        desc: 'Optuna, Keras Tuner, NAS — useful for SageMaker training jobs.',
        origin: 'original',
      },
      {
        id: 43,
        title: 'Edge AI & Embedded Inference',
        short: 'Edge AI',
        tag: 'Edge ★',
        desc: 'ONNX, quantisation, TFLite, llama.cpp — Pi 5 + MobileNet + BitNet bare-metal.',
        origin: 'added',
        note: 'Added: edge deployment is a distinct skill; your Pi work started here',
      },
      {
        id: 44,
        title: 'Offline Audit Systems & Tamper-Evidence',
        short: 'Audit Infra',
        tag: 'Trust ★',
        desc: 'Hash chains, append-only logs, SQLite schemas — MP-C scales into full infra.',
        origin: 'added',
        note: 'Added: auditability is a professional discipline, not just a feature',
      },
    ],
  },
  {
    id: 'advanced',
    label: 'Advanced & Research',
    color: C.lime,
    icon: '◆',
    desc: 'RL, multimodal AI, time series, open source — the research frontier.',
    steps: [
      {
        id: 45,
        title: 'Reinforcement Learning Fundamentals',
        short: 'RL',
        tag: 'RL',
        desc: 'Q-learning, RLHF — how modern LLMs are aligned; know the mechanism.',
        origin: 'original',
      },
      {
        id: 46,
        title: 'Time Series Forecasting',
        short: 'Time Series',
        tag: 'ML',
        desc: 'ARIMA, Prophet, LSTMs on time data — temporal disinformation patterns.',
        origin: 'original',
      },
      {
        id: 47,
        title: 'Multimodal AI: Text + Vision + Audio',
        short: 'Multimodal',
        tag: 'Research ★',
        desc: 'CLIP, LLaVA, fusion architectures — CDDBS Phase 3 is inherently multimodal.',
        origin: 'original',
      },
      {
        id: 48,
        title: 'AI in Real Industries',
        short: 'Industry AI',
        tag: 'Domain',
        desc: 'Security, finance, health case studies — maps to Fraunhofer AISEC.',
        origin: 'original',
      },
      {
        id: 49,
        title: 'Contribute to Open Source AI',
        short: 'OSS Contrib',
        tag: 'Community ★',
        desc: 'PRs to CDDBS, Pattern Atlas, upstream tools — career signal for trust orgs.',
        origin: 'original',
      },
      {
        id: 50,
        title: 'Publish Research & Engage Community',
        short: 'Publish',
        tag: 'Community ★',
        desc: 'arXiv, blog posts, talks — SNV, HIIG, AI Forensics all value public scholarship.',
        origin: 'original',
      },
    ],
  },
];

/** Canonical flat list of all 50 steps with phase metadata merged in. */
export const ALL_STEPS = PHASES.flatMap((p) =>
  p.steps.map((s) => ({
    ...s,
    phaseId: p.id,
    phaseColor: p.color,
    phaseLabel: p.label,
  })),
);

/** Ordered list of phase ids for layout computations. */
export const PHASE_ORDER = [
  'foundation',
  'classical_ml',
  'deep_learning',
  'nlp',
  'ai_trust',
  'infra',
  'advanced',
] as const;
